import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, IconButton,
  Snackbar, Alert, Tooltip, CircularProgress, Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { apiUrl } from '../../../../constants';
import useCheckToken from '../../../../hooks/useCheckToken';
import useExchangeToken from '../../../../hooks/useExchangeToken';

// ─── constants ───────────────────────────────────────────────────────────────
const FONT = "'CPF Imm Sook', sans-serif";

const TABS = [
  { label: 'Master',   key: 'master' },
  { label: 'Baseline', key: 'baseline' },
  { label: 'CL',       key: 'cl' },
  { label: 'Machine',  key: 'machine' },
];

const ENDPOINT = {
  master:   apiUrl.SIGMA_GIVEAWAY_MASTER,
  baseline: apiUrl.SIGMA_GIVEAWAY_BASELINE,
  cl:       apiUrl.SIGMA_GIVEAWAY_CL,
  machine:  apiUrl.SIGMA_GIVEAWAY_MACHINE,
};

// ─── column definitions per tab ──────────────────────────────────────────────
const BASE_COLS = [
  { field: 'plant_id',            headerName: 'Plant',    width: 80 },
  { field: 'factory',             headerName: 'Factory',  width: 80 },
  { field: 'line',                headerName: 'Line',     width: 80 },
  { field: 'material_description',headerName: 'Material', flex: 1, minWidth: 160 },
];

const COLUMNS = {
  master: [
    ...BASE_COLS,
    { field: 'std',      headerName: 'Std.',  width: 80,  type: 'number' },
    { field: 'LSL',      headerName: 'LSL',   width: 80,  type: 'number' },
    { field: 'USL',      headerName: 'USL',   width: 80,  type: 'number' },
    { field: 'check_on', headerName: 'Check-on', flex: 1, minWidth: 200 },
  ],
  baseline: [
    ...BASE_COLS,
    { field: 'baseline', headerName: 'Baseline', width: 100, type: 'number' },
    { field: 'target',   headerName: 'Target',   width: 100, type: 'number' },
    { field: 'check_on', headerName: 'Check-on', flex: 1, minWidth: 200 },
  ],
  cl: [
    ...BASE_COLS,
    { field: 'CL',       headerName: 'CL',    width: 90,  type: 'number' },
    { field: 'UCL',      headerName: 'UCL',   width: 90,  type: 'number' },
    { field: 'LCL',      headerName: 'LCL',   width: 90,  type: 'number' },
    { field: 'sigma',    headerName: 'Sigma', width: 90,  type: 'number' },
    {
      field: 'created_at', headerName: 'Created At', width: 160,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : '',
    },
    {
      field: 'updated_at', headerName: 'Updated At', width: 160,
      valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : '',
    },
  ],
  machine: [
    ...BASE_COLS,
    { field: 'machine_type', headerName: 'Type',    width: 120 },
    { field: 'machine_name', headerName: 'Machine', width: 140 },
    { field: 'check_on',     headerName: 'Check-on', flex: 1, minWidth: 200 },
  ],
};

// ─── empty form objects per tab ───────────────────────────────────────────────
const EMPTY_FORM = {
  master:   { plant_id: '', factory: '', line: '', material_description: '', std: '', LSL: '', USL: '', check_on: '' },
  baseline: { plant_id: '', factory: '', line: '', material_description: '', baseline: '', target: '', check_on: '' },
  cl:       { plant_id: '', factory: '', line: '', material_description: '', CL: '', UCL: '', LCL: '', sigma: '' },
  machine:  { plant_id: '', factory: '', line: '', material_description: '', machine_type: '', machine_name: '', check_on: '' },
};

// ─── form field definitions ───────────────────────────────────────────────────
const FORM_FIELDS = {
  master: [
    { name: 'plant_id',            label: 'Plant ID',            type: 'text' },
    { name: 'factory',             label: 'Factory',             type: 'text' },
    { name: 'line',                label: 'Line',                type: 'text' },
    { name: 'material_description',label: 'Material Description',type: 'text', fullWidth: true },
    { name: 'std',                 label: 'Std.',                type: 'number' },
    { name: 'LSL',                 label: 'LSL',                 type: 'number' },
    { name: 'USL',                 label: 'USL',                 type: 'number' },
    { name: 'check_on',            label: 'Check-on',            type: 'text', fullWidth: true },
  ],
  baseline: [
    { name: 'plant_id',            label: 'Plant ID',            type: 'text' },
    { name: 'factory',             label: 'Factory',             type: 'text' },
    { name: 'line',                label: 'Line',                type: 'text' },
    { name: 'material_description',label: 'Material Description',type: 'text', fullWidth: true },
    { name: 'baseline',            label: 'Baseline',            type: 'number' },
    { name: 'target',              label: 'Target',              type: 'number' },
    { name: 'check_on',            label: 'Check-on',            type: 'text', fullWidth: true },
  ],
  cl: [
    { name: 'plant_id',            label: 'Plant ID',            type: 'text' },
    { name: 'factory',             label: 'Factory',             type: 'text' },
    { name: 'line',                label: 'Line',                type: 'text' },
    { name: 'material_description',label: 'Material Description',type: 'text', fullWidth: true },
    { name: 'CL',                  label: 'CL (Center Line)',    type: 'number' },
    { name: 'UCL',                 label: 'UCL',                 type: 'number' },
    { name: 'LCL',                 label: 'LCL',                 type: 'number' },
    { name: 'sigma',               label: 'Sigma',               type: 'number' },
  ],
  machine: [
    { name: 'plant_id',            label: 'Plant ID',            type: 'text' },
    { name: 'factory',             label: 'Factory',             type: 'text' },
    { name: 'line',                label: 'Line',                type: 'text' },
    { name: 'material_description',label: 'Material Description',type: 'text', fullWidth: true },
    { name: 'machine_type',        label: 'Machine Type',        type: 'text' },
    { name: 'machine_name',        label: 'Machine Name',        type: 'text' },
    { name: 'check_on',            label: 'Check-on',            type: 'text', fullWidth: true },
  ],
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const authHeader = (token) => ({ Authorization: `Bearer ${token}` });

// ─── sub-components ───────────────────────────────────────────────────────────
/** Generic add/edit dialog */
const RecordDialog = ({ open, mode, tabKey, form, onChange, onSave, onClose, saving }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ fontFamily: FONT }}>
      {mode === 'add' ? 'เพิ่ม' : 'แก้ไข'} {tabKey.charAt(0).toUpperCase() + tabKey.slice(1)}
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ pt: 1 }}>
        {(FORM_FIELDS[tabKey] || []).map((f) => (
          <Grid item xs={f.fullWidth ? 12 : 6} key={f.name}>
            <TextField
              label={f.label}
              type={f.type}
              value={form[f.name] ?? ''}
              onChange={(e) => onChange(f.name, e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={f.type === 'number' ? { shrink: true } : undefined}
            />
          </Grid>
        ))}
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={saving}>ยกเลิก</Button>
      <Button
        variant="contained"
        onClick={onSave}
        disabled={saving}
        startIcon={saving ? <CircularProgress size={16} /> : null}
      >
        บันทึก
      </Button>
    </DialogActions>
  </Dialog>
);

/** Confirm delete dialog */
const DeleteDialog = ({ open, onConfirm, onClose, deleting }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs">
    <DialogTitle sx={{ fontFamily: FONT }}>ยืนยันการลบ</DialogTitle>
    <DialogContent>
      <Typography sx={{ fontFamily: FONT }}>ต้องการลบรายการนี้ใช่หรือไม่?</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={deleting}>ยกเลิก</Button>
      <Button
        variant="contained"
        color="error"
        onClick={onConfirm}
        disabled={deleting}
        startIcon={deleting ? <CircularProgress size={16} /> : null}
      >
        ลบ
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Main component ───────────────────────────────────────────────────────────
const SigmaTrackMasterPage = () => {
  useCheckToken();
  const exchangeToken = useExchangeToken();
  const ac_token = useSelector((s) => s.auth.access_token);

  // ── filter ──
  const [filter, setFilter] = useState({ plant_id: '', factory: '', line: '', material_description: '' });

  // ── tab ──
  const [tabIdx, setTabIdx] = useState(0);
  const tabKey = TABS[tabIdx].key;

  // ── table rows per tab (cached after first load) ──
  const [rows, setRows] = useState({ master: [], baseline: [], cl: [], machine: [] });
  const [loading, setLoading] = useState(false);

  // ── dialog ──
  const [dialog, setDialog] = useState({ open: false, mode: 'add', form: {}, rowId: null });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, rowId: null });
  const [deleting, setDeleting] = useState(false);

  // ── snackbar ──
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    let token = ac_token;
    if (!token) {
      token = await exchangeToken().catch(() => null);
      if (!token) return;
    }
    setLoading(true);
    try {
      const params = {};
      if (filter.plant_id)            params.plant_id = filter.plant_id;
      if (filter.factory)             params.factory  = filter.factory;
      if (filter.line)                params.line     = filter.line;
      if (filter.material_description) params.material_description = filter.material_description;

      const res = await axios.get(ENDPOINT[tabKey], {
        headers: authHeader(token),
        params,
      });
      setRows((prev) => ({ ...prev, [tabKey]: res.data || [] }));
    } catch (err) {
      showSnack(err?.response?.data?.detail || 'โหลดข้อมูลล้มเหลว', 'error');
    } finally {
      setLoading(false);
    }
  }, [ac_token, exchangeToken, filter, tabKey]);

  // ── open add dialog ────────────────────────────────────────────────────────
  const handleAdd = () =>
    setDialog({ open: true, mode: 'add', form: { ...EMPTY_FORM[tabKey] }, rowId: null });

  // ── open edit dialog ───────────────────────────────────────────────────────
  const handleEdit = useCallback((row) => {
    setDialog({ open: true, mode: 'edit', form: { ...row }, rowId: row.id });
  }, []);

  // ── open delete confirm ────────────────────────────────────────────────────
  const handleDeletePrompt = useCallback((id) =>
    setDeleteDialog({ open: true, rowId: id }), []);

  // ── form change ────────────────────────────────────────────────────────────
  const handleFormChange = (field, value) =>
    setDialog((prev) => ({ ...prev, form: { ...prev.form, [field]: value } }));

  // ── save (add / edit) ──────────────────────────────────────────────────────
  const handleSave = async () => {
    let token = ac_token;
    if (!token) { token = await exchangeToken().catch(() => null); }
    if (!token) return;

    setSaving(true);
    try {
      const payload = { ...dialog.form };
      // coerce numeric strings
      (FORM_FIELDS[tabKey] || []).forEach(({ name, type }) => {
        if (type === 'number' && payload[name] !== '') {
          payload[name] = parseFloat(payload[name]);
        }
      });

      if (dialog.mode === 'add') {
        await axios.post(ENDPOINT[tabKey], payload, { headers: authHeader(token) });
        showSnack('เพิ่มข้อมูลสำเร็จ');
      } else {
        await axios.patch(`${ENDPOINT[tabKey]}${dialog.rowId}/`, payload, { headers: authHeader(token) });
        showSnack('แก้ไขข้อมูลสำเร็จ');
      }
      setDialog((prev) => ({ ...prev, open: false }));
      fetchData();
    } catch (err) {
      showSnack(err?.response?.data ? JSON.stringify(err.response.data) : 'บันทึกล้มเหลว', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    let token = ac_token;
    if (!token) { token = await exchangeToken().catch(() => null); }
    if (!token) return;

    setDeleting(true);
    try {
      await axios.delete(`${ENDPOINT[tabKey]}${deleteDialog.rowId}/`, { headers: authHeader(token) });
      showSnack('ลบข้อมูลสำเร็จ');
      setDeleteDialog({ open: false, rowId: null });
      fetchData();
    } catch (err) {
      showSnack('ลบล้มเหลว', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── action column ──────────────────────────────────────────────────────────
  const actionColumn = {
    field: 'actions',
    headerName: '',
    width: 90,
    sortable: false,
    renderCell: (params) => (
      <Box>
        <Tooltip title="แก้ไข">
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="ลบ">
          <IconButton size="small" color="error" onClick={() => handleDeletePrompt(params.row.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  };

  const columns = [...(COLUMNS[tabKey] || []), actionColumn];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* ── Page title ── */}
      <Typography variant="h5" sx={{ fontFamily: FONT, fontWeight: 'bold', mb: 2 }}>
        Sigma Track — จัดการ Master Data
      </Typography>

      {/* ── Filter bar ── */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          mb: 2,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        }}
      >
        {[
          { key: 'plant_id',            label: 'Plant ID', width: 120 },
          { key: 'factory',             label: 'Factory',  width: 120 },
          { key: 'line',                label: 'Line',     width: 120 },
          { key: 'material_description',label: 'Material', width: 240 },
        ].map(({ key, label, width }) => (
          <TextField
            key={key}
            label={label}
            value={filter[key]}
            onChange={(e) => setFilter((prev) => ({ ...prev, [key]: e.target.value }))}
            size="small"
            sx={{ width }}
          />
        ))}
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={fetchData}
          disabled={loading}
          sx={{ height: 40 }}
        >
          ค้นหา
        </Button>
      </Box>

      {/* ── Main card ── */}
      <Box sx={{ bgcolor: '#fff', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', px: 2 }}>
          <Tabs
            value={tabIdx}
            onChange={(_, v) => setTabIdx(v)}
            sx={{ flex: 1 }}
          >
            {TABS.map((t) => (
              <Tab
                key={t.key}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    {t.label}
                    {rows[t.key].length > 0 && (
                      <Chip label={rows[t.key].length} size="small" color="primary" />
                    )}
                  </Box>
                }
                sx={{ fontFamily: FONT }}
              />
            ))}
          </Tabs>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{ ml: 2, fontFamily: FONT }}
          >
            เพิ่ม {TABS[tabIdx].label}
          </Button>
        </Box>

        {/* DataGrid */}
        <Box sx={{ height: 480 }}>
          <DataGrid
            rows={rows[tabKey]}
            columns={columns}
            loading={loading}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
            density="compact"
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': { bgcolor: '#fafafa', fontFamily: FONT },
              '& .MuiDataGrid-cell': { fontFamily: FONT },
            }}
          />
        </Box>
      </Box>

      {/* ── Dialogs ── */}
      <RecordDialog
        open={dialog.open}
        mode={dialog.mode}
        tabKey={tabKey}
        form={dialog.form}
        onChange={handleFormChange}
        onSave={handleSave}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
        saving={saving}
      />

      <DeleteDialog
        open={deleteDialog.open}
        onConfirm={handleDelete}
        onClose={() => setDeleteDialog({ open: false, rowId: null })}
        deleting={deleting}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SigmaTrackMasterPage;
